import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PasswordField from "../field/PasswordField";
import { registerUser } from "../redux/features/registerSlice";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle";
import { useTranslation } from "react-i18next";
import { useModal } from "../hooks/ModalContext";

const RegisterModal = ({ isActive, onClose, onLogin, onVerifyOtp }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { status, error } = useSelector((state) => state.register);
  const lang = useSelector((state) => state.language.lang);
  const { setEmailForOtp, setPasswordForOtp } = useModal();

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [errors, setErrors] = useState({}); // ✅ validation errors

  const modalRef = useRef(null);

  useEffect(() => {
    if (!modalRef.current) return;

    const modalInstance = bootstrap.Modal.getOrCreateInstance(modalRef.current, {
      backdrop: true,
      keyboard: true,
    });

    modalRef.current.removeEventListener("hidden.bs.modal", handleModalHidden);
    modalRef.current.addEventListener("hidden.bs.modal", handleModalHidden);

    if (isActive) {
      setFirstname("");
      setLastname("");
      setEmail("");
      setPassword("");
      setPasswordConfirmation("");
      setErrors({}); // clear validation errors
      modalInstance.show();
    } else {
      modalInstance.hide();
    }

    return () => {
      modalRef.current?.removeEventListener("hidden.bs.modal", handleModalHidden);
    };
  }, [isActive]);

  const handleModalHidden = () => {
    setSubmitted(false); // <-- Prevent stale flag from opening OTP again
    onClose?.();
  };


  const handleSwitchToLogin = () => {
    if (modalRef.current) {
      const modalInstance = bootstrap.Modal.getInstance(modalRef.current);
      modalInstance?.hide();

      modalRef.current.addEventListener(
        "hidden.bs.modal",
        () => {
          onLogin?.();
        },
        { once: true }
      );
    } else {
      onLogin?.();
    }
  };

  useEffect(() => {
    if (!submitted) return;                 // <-- Only act if submitted
    if (status !== "success") return;       // <-- Prevent unwanted re-triggers
    if (!modalRef.current) return;

    const modalInstance = bootstrap.Modal.getInstance(modalRef.current);
    modalInstance?.hide();

    setTimeout(() => {
      setEmailForOtp(email);
      setPasswordForOtp(password);        // 🔥 NEW
      onVerifyOtp?.();

      setSubmitted(false);
      dispatch({ type: "register/resetState" });
    }, 250);

  }, [submitted, status, email, onVerifyOtp, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();

    setSubmitted(true); // <-- ADD THIS

    let newErrors = {};

    if (!firstname.trim()) newErrors.firstname = t("This_field_is_required");
    if (!lastname.trim()) newErrors.lastname = t("This_field_is_required");
    if (!email.trim()) newErrors.email = t("This_field_is_required");
    if (!password.trim()) newErrors.password = t("This_field_is_required");
    if (!passwordConfirmation.trim())
      newErrors.passwordConfirmation = t("This_field_is_required");

    if (
      password.trim() &&
      passwordConfirmation.trim() &&
      password !== passwordConfirmation
    ) {
      newErrors.passwordConfirmation = t("Passwords_do_not_match");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitted(false); // <-- reset when validation fails
      return;
    }

    setErrors({});

    const userData = {
      first_name: firstname,
      last_name: lastname,
      email,
      password,
      password_confirmation: passwordConfirmation,
    };

    dispatch(registerUser({ userData, lang }));
  };


  return (
    <div className="modal fade" ref={modalRef} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("welcome")}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                const modalInstance = bootstrap.Modal.getInstance(modalRef.current);
                modalInstance?.hide();
              }}
            ></button>
          </div>

          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="mb-3 col-12 col-sm-6">
                  <label className="form-label">{t("Firstname")}</label>
                  <input
                    type="text"
                    className={`form-control ${errors.firstname ? "is-invalid" : ""}`}
                    placeholder="Enter your firstname"
                    value={firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                  />
                  {errors.firstname && (
                    <div className="invalid-feedback">{errors.firstname}</div>
                  )}
                </div>

                <div className="mb-3 col-12 col-sm-6">
                  <label className="form-label">{t("Lastname")}</label>
                  <input
                    type="text"
                    className={`form-control ${errors.lastname ? "is-invalid" : ""}`}
                    placeholder="Enter your lastname"
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value)}
                  />
                  {errors.lastname && (
                    <div className="invalid-feedback">{errors.lastname}</div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">{t("Email")}</label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>

              <PasswordField
                password={password}
                setPassword={(v) => {
                  setPassword(v);
                  setErrors((prev) => ({ ...prev, password: "" }));
                }}
                error={errors.password}
                clearError={() =>
                  setErrors((prev) => ({ ...prev, password: "" }))
                }
              />

              <div className="mb-3 position-relative">
                <label className="form-label">{t("Confirm_Password")}</label>

                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`form-control ${errors.passwordConfirmation ? "is-invalid" : ""
                      }`}
                    placeholder={t("Re_enter_your_password")}
                    value={passwordConfirmation}
                    onChange={(e) => {
                      setPasswordConfirmation(e.target.value);

                      // Clear validation error dynamically
                      setErrors((prev) => ({
                        ...prev,
                        passwordConfirmation: "",
                      }));
                    }}
                  />

                  {/* Eye Toggle Button (exact same style as PasswordField) */}
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                    }}
                  >
                    <i
                      className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                        }`}
                    />
                  </button>

                  {errors.passwordConfirmation && (
                    <div className="invalid-feedback">{errors.passwordConfirmation}</div>
                  )}
                </div>
              </div>

              {error && <p className="text-danger">{error}</p>}

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={status === "loading"}
              >
                {status === "loading" ? "Registering..." : "Register"}
              </button>
            </form>

            <p className="mt-3 text-center">
              {t("Already_have_an_account?")}{" "}
              <button className="btn btn-link p-0" onClick={handleSwitchToLogin}>
                {t("Login")}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;
